import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import { Plus, Loader2, ExternalLink, Save } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button, Input, Textarea, Avatar } from '@/components/ui';
import { ProfilePreview, AddWidgetModal, EditWidgetModal, WidgetList } from '@/components/profile';
import { usePage } from '@/hooks/usePage';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui';
import { uploadFile, deleteFile } from '@/services/storage';
import type { WidgetType, WidgetData, Widget } from '@/types';

const ProfileEditor = () => {
  const { t } = useTranslation();
  const { page, loading, update, addWidget, editWidget, removeWidget, reorderWidgets } = usePage();
  const { user } = useAuth();
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    bio: '',
  });

  // Initialize form data when page loads
  useEffect(() => {
    if (page) {
      setFormData({
        title: page.title || '',
        bio: page.bio || '',
      });
    }
  }, [page]);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user) return;

    setIsUploadingAvatar(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      });

      const ext = file.name.split('.').pop() || 'jpg';
      const path = `profile/${user.uid}/${Date.now()}.${ext}`;

      // Delete old avatar if it's a Firebase Storage URL
      if (page?.avatarUrl?.includes('firebasestorage.googleapis.com')) {
        const oldPath = decodeURIComponent(
          page.avatarUrl.split('/o/')[1]?.split('?')[0] || ''
        );
        if (oldPath) await deleteFile(oldPath);
      }

      const downloadUrl = await uploadFile(path, compressed);
      await update({ avatarUrl: downloadUrl });

      addToast({
        type: 'success',
        title: t('profile.avatarUpdated'),
        message: t('profile.avatarUpdatedMessage'),
      });
    } catch {
      addToast({
        type: 'error',
        title: t('profile.failedToUploadAvatar'),
        message: t('profile.failedToUploadAvatarMessage'),
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!page) return;

    setIsSaving(true);
    try {
      await update({
        title: formData.title,
        bio: formData.bio,
      });
      addToast({
        type: 'success',
        title: t('profile.profileSaved'),
        message: t('profile.profileSavedMessage'),
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: t('profile.failedToSave'),
        message: t('profile.failedToSaveMessage'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWidget = async (type: WidgetType, data: WidgetData) => {
    try {
      await addWidget(type, data);
      addToast({
        type: 'success',
        title: t('profile.widgetAdded'),
        message: t('profile.widgetAddedMessage'),
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: t('profile.failedToAddWidget'),
        message: t('profile.failedToSaveMessage'),
      });
    }
  };

  const handleDeleteWidget = async (id: string) => {
    try {
      await removeWidget(id);
      addToast({
        type: 'success',
        title: t('profile.widgetDeleted'),
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: t('profile.failedToDeleteWidget'),
      });
    }
  };

  const handleEditWidget = (widget: Widget) => {
    setEditingWidget(widget);
  };

  const handleSaveEditedWidget = async (widgetId: string, data: WidgetData) => {
    try {
      await editWidget(widgetId, { data });
      addToast({
        type: 'success',
        title: t('profile.widgetUpdated'),
        message: t('profile.widgetUpdatedMessage'),
      });
      setEditingWidget(null);
    } catch {
      addToast({
        type: 'error',
        title: t('profile.failedToUpdateWidget'),
        message: t('profile.failedToSaveMessage'),
      });
    }
  };

  if (loading || !page) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Editor Panel */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('profile.title')}</h1>
              <p className="text-gray-500">
                {t('profile.customizeSubtitle')}{' '}
                <a
                  href={`https://klipr.it/@${page.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime-600 hover:text-lime-700"
                >
                  klipr.it/@{page.username}
                </a>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`/@${page.username}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" leftIcon={<ExternalLink className="w-4 h-4" />}>
                  {t('profile.preview')}
                </Button>
              </a>
              <Button
                variant="primary"
                leftIcon={<Save className="w-4 h-4" />}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t('profile.saveChanges')}
              </Button>
            </div>
          </div>

          {/* Profile Settings */}
          <Card variant="default" padding="lg" className="mb-6">
            <CardTitle className="mb-6">{t('profile.profileSettings')}</CardTitle>

            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="relative">
                  <Avatar
                    src={page.avatarUrl || undefined}
                    name={page.title}
                    size="xl"
                  />
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  isLoading={isUploadingAvatar}
                >
                  {t('profile.change')}
                </Button>
              </div>

              <div className="flex-1 space-y-4">
                <Input
                  label={t('profile.displayName')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Your name"
                />
                <Textarea
                  label={t('profile.bio')}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder={t('profile.bioPlaceholder')}
                  rows={3}
                />
              </div>
            </div>
          </Card>

          {/* Widgets */}
          <Card variant="default" padding="lg">
            <div className="flex items-center justify-between mb-6">
              <CardTitle>{t('profile.widgets')}</CardTitle>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setIsAddWidgetOpen(true)}
              >
                {t('profile.addWidget')}
              </Button>
            </div>

            <WidgetList
              widgets={page.widgets || []}
              onReorder={reorderWidgets}
              onEdit={handleEditWidget}
              onDelete={handleDeleteWidget}
            />
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="hidden lg:block flex-shrink-0 sticky top-24 h-fit">
          <p className="text-sm font-medium text-gray-500 mb-4 text-center">
            {t('profile.livePreview')}
          </p>
          <ProfilePreview page={{ ...page, ...formData }} />
        </div>
      </div>

      {/* Add Widget Modal */}
      <AddWidgetModal
        isOpen={isAddWidgetOpen}
        onClose={() => setIsAddWidgetOpen(false)}
        onAdd={handleAddWidget}
      />

      {/* Edit Widget Modal */}
      {editingWidget && (
        <EditWidgetModal
          isOpen={!!editingWidget}
          onClose={() => setEditingWidget(null)}
          widget={editingWidget}
          onSave={handleSaveEditedWidget}
        />
      )}
    </DashboardLayout>
  );
};

export default ProfileEditor;
